import { useState } from "react";

import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from "@heroicons/react/24/outline";

import { IPart } from "../../models/Part";
import { IListingOptimisation } from "../../models/listingOptimisation/IListingOptimisation";
import classNames from "../../utils/classNames";
import UnlistedKTypeUpdate from "../ebayListings/unlistedTable/UnlistedKTypeUpdate";
import { MIN_IMAGE_COUNT } from "../ebayListings/listedTable/listingScore";
import { IQueueRowSignals } from "./queueRowSignals";
import { DONE_SECTION_CLASSES } from "./drawerSectionState";
import { scoreToneClasses } from "./QueueRow";

export type PartNumberField = "partNumber" | "oePartNumber";

interface IFocusDrawerHealthSectionProps {
  part: IPart;
  signals: IQueueRowSignals;
  optimisation: IListingOptimisation | undefined;
  onSavePartNumber: (field: PartNumberField, value: string) => void;
  savingPartNumber: boolean;
  onManagePhotos: () => void;
  onOpenSpecifics: () => void;
  onChangePolicies: () => void;
  onRegenerateDescription: () => void;
  regenerating: boolean;
  onSetGrade: () => void;
  /** Nothing fixable is missing and nothing blocks the relist: the section turns green. */
  done: boolean;
}

type LineTone = "warn" | "blocked" | "ok";

const LINE_CLASSES: Record<LineTone, string> = {
  warn: "border-amber-200 bg-amber-50 text-amber-900",
  blocked: "border-red-200 bg-red-50 text-red-800",
  ok: "border-gray-200 bg-white text-gray-700",
};

const BUTTON_CLASSES: Record<LineTone, string> = {
  warn: "border-amber-300 bg-white text-amber-900 hover:bg-amber-100",
  blocked: "border-red-300 bg-white text-red-800 hover:bg-red-100",
  ok: "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
};

function Line({ tone, children, action }: { tone: LineTone; children: React.ReactNode; action?: React.ReactNode }) {
  const Icon = tone === "ok" ? CheckCircleIcon : tone === "blocked" ? XCircleIcon : ExclamationTriangleIcon;
  return (
    <li className={classNames("flex items-center gap-2.5 rounded-md border px-2.5 py-2 text-xs", LINE_CLASSES[tone])}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{children}</span>
      {action}
    </li>
  );
}

function LineButton({ tone, onClick, children, disabled }: { tone: LineTone; onClick: () => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={classNames("inline-flex h-7 shrink-0 items-center gap-1 rounded-md border px-2.5 text-[11px] font-semibold disabled:opacity-50", BUTTON_CLASSES[tone])}
    >
      {children}
    </button>
  );
}

function PartNumberInput({
  label,
  field,
  onSave,
  saving,
}: {
  label: string;
  field: PartNumberField;
  onSave: (field: PartNumberField, value: string) => void;
  saving: boolean;
}) {
  const [value, setValue] = useState("");
  const trimmed = value.trim();

  return (
    <form
      className="flex shrink-0 items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed) onSave(field, trimmed);
      }}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={label}
        aria-label={label}
        className="h-7 w-32 rounded-md border border-amber-300 px-2 text-xs focus:border-amber-500 focus:ring-0"
      />
      <button
        type="submit"
        disabled={!trimmed || saving}
        className="inline-flex h-7 items-center rounded-md border border-amber-300 bg-white px-2.5 text-[11px] font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
      >
        {saving ? <ArrowPathIcon className="h-3 w-3 animate-spin" aria-hidden="true" /> : "Add"}
      </button>
    </form>
  );
}

/**
 * The listing score as a checklist, one line per thing to fix with the button that fixes it.
 * Part numbers, KType and grade save to the part straight away; photos, specifics and policies
 * open their editors; the description regenerates from the category template on the part only.
 */
export default function FocusDrawerHealthSection({
  part,
  signals,
  optimisation,
  onSavePartNumber,
  savingPartNumber,
  onManagePhotos,
  onOpenSpecifics,
  onChangePolicies,
  onRegenerateDescription,
  regenerating,
  onSetGrade,
  done,
}: IFocusDrawerHealthSectionProps) {
  const { score } = signals;
  const weightOf = (key: string) => score.breakdown.find((field) => field.key === key)?.weight ?? 0;
  const missing = new Set(score.missing);
  const scoredFields = score.breakdown.filter((field) => field.applicable && !field.fixable).length;
  const imageCount = signals.imageCount ?? 0;
  const bgStaged = !!optimisation?.removeBackgroundFirstImage;

  const aspectValue = (name: string) => part.aspects?.find((aspect) => aspect.name?.toLowerCase() === name.toLowerCase())?.value;
  const specificsSummary = [
    part.vehicle?.kType ? `KType ${part.vehicle.kType}` : null,
    aspectValue("Brand") ? `Brand ${aspectValue("Brand")}` : null,
    part.partNumber ? `MPN ${part.partNumber}` : null,
  ].filter(Boolean);

  const offer = part.offers?.[0];
  const hasPolicies = !!(offer?.fulfillmentPolicyId && offer?.paymentPolicyId && offer?.returnPolicyId);
  const policiesStaged = !!(optimisation?.newFulfillmentPolicyId || optimisation?.newPaymentPolicyId || optimisation?.newReturnPolicyId || optimisation?.newLocationKey);

  return (
    <section aria-label="Listing health" className={classNames("space-y-2 border-b px-6 py-4", done ? DONE_SECTION_CLASSES : "border-gray-200")}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-900">
          Listing health
          {done ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
              <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Done
            </span>
          ) : null}
        </h3>
        <span className="inline-flex items-center gap-1.5">
          <span className={classNames("rounded-full px-2 py-0.5 text-[13px] font-bold", scoreToneClasses(score.percent))}>{score.percent}%</span>
          <span className="text-[11px] text-gray-500">
            {score.present} of {score.total}
            {scoredFields > 0 ? ` · age and price count for ${scoredFields}` : ""}
          </span>
        </span>
      </div>

      <ul className="space-y-1">
        {signals.relistBlocked &&
          signals.relistErrors.map((error) => (
            <Line
              key={error}
              tone="blocked"
              action={
                /image/i.test(error) ? (
                  <LineButton tone="blocked" onClick={onManagePhotos}>Manage photos</LineButton>
                ) : /polic|location/i.test(error) ? (
                  <LineButton tone="blocked" onClick={onChangePolicies}>Change</LineButton>
                ) : (
                  <LineButton tone="blocked" onClick={onOpenSpecifics}>Specifics</LineButton>
                )
              }
            >
              <strong>Can&apos;t relist</strong> — {error}
            </Line>
          ))}

        {missing.has("MPN") && (
          <Line tone="warn" action={<PartNumberInput label="MPN" field="partNumber" onSave={onSavePartNumber} saving={savingPartNumber} />}>
            <strong>MPN</strong> missing — worth {weightOf("MPN")} points
          </Line>
        )}
        {missing.has("OEMPN") && (
          <Line tone="warn" action={<PartNumberInput label="OE part number" field="oePartNumber" onSave={onSavePartNumber} saving={savingPartNumber} />}>
            <strong>OE part number</strong> missing — worth {weightOf("OEMPN")} points
          </Line>
        )}
        {missing.has("KType") && (
          <Line tone="warn" action={part.vehicle ? <div className="w-44 shrink-0"><UnlistedKTypeUpdate part={part} /></div> : undefined}>
            <strong>KType</strong> missing — worth {weightOf("KType")} points
          </Line>
        )}
        {missing.has("Brand") && (
          <Line tone="warn" action={<LineButton tone="warn" onClick={onOpenSpecifics}>Specifics</LineButton>}>
            <strong>Brand</strong> missing — worth {weightOf("Brand")} points
          </Line>
        )}
        {missing.has("Grade") && (
          <Line tone="warn" action={<LineButton tone="warn" onClick={onSetGrade}>Set grade</LineButton>}>
            <strong>Grade</strong> missing — worth {weightOf("Grade")} point
          </Line>
        )}
        {(missing.has("Make") || missing.has("Model") || missing.has("VIN")) && (
          <Line tone="warn">
            <strong>Vehicle</strong> is missing {["Make", "Model", "VIN"].filter((key) => missing.has(key)).join(", ")} — set on the vehicle record
          </Line>
        )}

        <Line
          tone={imageCount > MIN_IMAGE_COUNT ? "ok" : "warn"}
          action={<LineButton tone={imageCount > MIN_IMAGE_COUNT ? "ok" : "warn"} onClick={onManagePhotos}>Manage photos</LineButton>}
        >
          <strong>
            {imageCount} photo{imageCount === 1 ? "" : "s"}
          </strong>
          {imageCount > MIN_IMAGE_COUNT ? "" : ` — eBay rewards ${MIN_IMAGE_COUNT} or more`}
          {bgStaged ? "; background removal staged for the 1st" : ""}
        </Line>

        <Line tone="ok" action={<LineButton tone="ok" onClick={onOpenSpecifics}>Specifics</LineButton>}>
          {specificsSummary.length > 0 ? specificsSummary.join(", ") : "Item specifics"}
        </Line>

        <Line
          tone={hasPolicies || policiesStaged ? "ok" : "warn"}
          action={<LineButton tone={hasPolicies || policiesStaged ? "ok" : "warn"} onClick={onChangePolicies}>Change</LineButton>}
        >
          <strong>Policies</strong> {policiesStaged ? "— new policies staged" : hasPolicies ? "set on the listing" : "fall back to the category defaults"}
        </Line>

        <Line tone="ok" action={<LineButton tone="ok" onClick={onRegenerateDescription} disabled={regenerating}>{regenerating ? "Regenerating…" : "Regenerate"}</LineButton>}>
          <strong>Description</strong> from the {part.category?.name ?? "category"} template — regenerates on the part, goes live at relist
        </Line>
      </ul>
    </section>
  );
}
